/**
 * Indicateur visuel de sauvegarde automatique.
 * Affiche l'état actuel : idle / saving / saved / error,
 * + un compteur de modifications en attente,
 * + l'heure de la dernière sauvegarde réussie.
 */
import React, { useEffect, useState } from 'react';
import { Cloud, CloudOff, Loader2, CheckCircle2 } from 'lucide-react';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface Props {
  status: AutoSaveStatus;
  lastSaveTime: Date | null;
  pendingCount: number;
  className?: string;
}

const AutoSaveIndicator: React.FC<Props> = ({ status, lastSaveTime, pendingCount, className }) => {
  const [now, setNow] = useState(Date.now());

  // Re-render every 30s pour MAJ "il y a X min"
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const formatLastSave = (d: Date | null): string => {
    if (!d) return '';
    const diffSec = Math.floor((now - d.getTime()) / 1000);
    if (diffSec < 5) return 'à l\'instant';
    if (diffSec < 60) return `il y a ${diffSec}s`;
    const min = Math.floor(diffSec / 60);
    if (min < 60) return `il y a ${min} min`;
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  // Statut courant : si on a des modifications en attente, on prévaut "à sauver"
  let icon: React.ReactNode;
  let label: string;
  let colorClass: string;

  if (status === 'saving') {
    icon = <Loader2 size={14} className="animate-spin" />;
    label = 'Sauvegarde…';
    colorClass = 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700';
  } else if (status === 'error') {
    icon = <CloudOff size={14} />;
    label = `Erreur — ${pendingCount} en attente`;
    colorClass = 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700';
  } else if (pendingCount > 0) {
    icon = <Cloud size={14} />;
    label = `${pendingCount} à sauver…`;
    colorClass = 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700';
  } else if (status === 'saved' && lastSaveTime) {
    icon = <CheckCircle2 size={14} />;
    label = `Sauvegardé ${formatLastSave(lastSaveTime)}`;
    colorClass = 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700';
  } else {
    return null; // idle, rien à afficher
  }

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium ${colorClass} ${className || ''}`}
      title="Sauvegarde automatique : vos notes sont enregistrées 2 secondes après votre dernière modification."
    >
      {icon}
      <span>{label}</span>
    </div>
  );
};

export default AutoSaveIndicator;
